// Sezione "Dichiarazioni della cliente" — visualizza le risposte del
// questionario pre-analisi cute (passo 3a) all'interno del dettaglio
// referto. Mostra solo le risposte effettivamente date. Marca le voci
// "non confermate dal professionista" quando il tag corrispondente è
// stato rifiutato nel dialog 3b-2.
//
// INTERVENTO 3 / passo 3c — sola lettura. Nessuna scrittura.

import React from "react";
import { MessageCircle, Info } from "lucide-react";

// Etichetta umana della domanda + etichetta umana per ciascuna risposta.
// Stesso ordine del questionario (CuteQuestionnaire.jsx) per coerenza
// visiva tra "compilazione" e "lettura".
const LABELS = [
  { id: "sensazione_cute",     label: "Percezione cute",         answers: { normale: "Normale", grassa: "Grassa", secca: "Secca", non_so: "Non saprei" } },
  { id: "prurito",             label: "Prurito",                 answers: { mai: "Mai", ogni_tanto: "Ogni tanto", spesso: "Spesso" } },
  { id: "desquamazione",       label: "Desquamazione / forfora", answers: { no: "No", leggera: "Leggera", evidente: "Evidente" } },
  { id: "frequenza_lavaggio",  label: "Frequenza lavaggio",      answers: { "1g": "1 giorno", "2g": "2 giorni", "3g_o_piu": "3 giorni o più" } },
  { id: "fastidio_cute",       label: "Fastidio cute",           answers: { no: "No", tensione: "Sensazione di tensione", bruciore: "Sensazione di bruciore", tatto: "Sensibilità al tocco" } },
  { id: "caduta",              label: "Caduta",                  answers: { no: "No", leggera: "Leggera", moderata: "Moderata", importante: "Importante" } },
  { id: "caduta_durata",       label: "Durata caduta",           answers: { "<1m": "Meno di 1 mese", "1-3m": "1-3 mesi", ">3m": "Più di 3 mesi" } },
  { id: "trattamenti_chimici", label: "Trattamenti chimici",     answers: { no: "No", colore: "Colore", schiariture: "Schiariture", permanente: "Permanente", piu: "Più trattamenti" } },
  { id: "prodotti_cute_in_uso",label: "Prodotti cute in uso",    answers: { no: "No", si: "Sì" } },
  { id: "fascia_eta",          label: "Fascia d'età",            answers: { "18-24":"18-24", "25-34":"25-34", "35-44":"35-44", "45-54":"45-54", "55-64":"55-64", "65+":"65+", no_risposta: "Preferisce non rispondere" } },
];

// Mappa tag rifiutato → id della domanda che lo ha prodotto. Quando il
// parrucchiere rifiuta un tag nel dialog 3b-2, la riga corrispondente
// nel questionario va marcata "non confermata".
const TAG_TO_QUESTION_ID = {
  caduta: "caduta",
  diradamento: "caduta",        // D6=importante → caduta + diradamento
  cute_sensibile: "fastidio_cute",
  rossore: "fastidio_cute",
};


export default function QuestionnaireDeclarations({ questionnaire, conflictResolution }) {
  if (!questionnaire || Object.keys(questionnaire).length === 0) {
    return null;
  }

  const rejected = new Set(conflictResolution?.rejected || []);
  // Mappa "quale domanda" è collegata a una decisione di rifiuto.
  const rejectedQuestionIds = new Set(
    [...rejected].map((tag) => TAG_TO_QUESTION_ID[tag]).filter(Boolean),
  );

  const rows = LABELS
    .map((q) => ({ q, value: questionnaire[q.id] }))
    .filter((r) => r.value !== undefined && r.value !== null && r.value !== "");

  if (rows.length === 0) return null;

  return (
    <div
      className="col-span-12 rounded-2xl border border-[#E5E5E0] bg-white p-8 animate-fade-up"
      data-testid="questionnaire-declarations"
    >
      <div className="flex items-center gap-2 mb-5">
        <MessageCircle className="w-4 h-4 text-[#C5A059]" />
        <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium">
          Dichiarazioni della cliente
        </p>
      </div>
      <p className="text-sm text-[#737373] mb-6 leading-relaxed">
        Risposte raccolte nel questionario pre-analisi. Non sono visibili
        nel PDF cliente: restano come traccia di lavoro del parrucchiere.
      </p>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
        {rows.map(({ q, value }) => {
          const answerLabel = q.answers?.[value] || String(value);
          const isUnconfirmed = rejectedQuestionIds.has(q.id);
          return (
            <div
              key={q.id}
              className="flex flex-col gap-1 py-2 border-b border-[#F0EFEA] last:border-b-0"
              data-testid={`declaration-${q.id}`}
            >
              <dt className="text-[11px] uppercase tracking-wider text-[#A3A3A3] font-medium">
                {q.label}
              </dt>
              <dd className="text-[15px] text-[#1A1A1A]">
                {answerLabel}
                {isUnconfirmed && (
                  <span
                    className="ml-2 inline-flex items-center gap-1 text-[11px] text-[#737373] italic font-normal"
                    data-testid={`declaration-${q.id}-unconfirmed`}
                  >
                    <Info className="w-3 h-3" />
                    (non confermata dal professionista)
                  </span>
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}
